import { useState, useEffect, useCallback } from 'react'
import { listProjects } from '../api/admin'
import type { AdminProject } from '../types'
import { LoadingSpinner } from '../components/LoadingSpinner'
import styles from './AdminProjectsTable.module.css'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  complete: 'Completo',
  generated: 'Generado',
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status
}

export function AdminProjectsTable() {
  const [projects, setProjects] = useState<AdminProject[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const LIMIT = 20

  const load = useCallback(async (p: number) => {
    setLoading(true)
    setError(null)
    try {
      const resp = await listProjects(p, LIMIT)
      setProjects(resp.items)
      setTotal(resp.total)
    } catch {
      setError('No se pudieron cargar los proyectos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(page) }, [load, page])

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  if (loading) {
    return (
      <div className={styles.centered}>
        <LoadingSpinner size="md" message="Cargando proyectos..." />
      </div>
    )
  }

  if (error) {
    return <p className={styles.error}>{error}</p>
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableScroll}>
        <table className={styles.table} aria-label="Tabla de proyectos">
          <thead>
            <tr>
              <th scope="col">Nombre</th>
              <th scope="col">Propietario</th>
              <th scope="col">Estado</th>
              <th scope="col">Docs generados</th>
              <th scope="col">Fecha</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.empty}>No hay proyectos registrados.</td>
              </tr>
            ) : (
              projects.map((p) => (
                <tr key={p.id}>
                  <td data-label="Nombre" className={styles.projectName}>{p.name}</td>
                  <td data-label="Propietario">{p.owner_email}</td>
                  <td data-label="Estado">
                    <span className={`${styles.badge} ${styles[`status_${p.status}`] ?? styles.statusDefault}`}>
                      {statusLabel(p.status)}
                    </span>
                  </td>
                  <td data-label="Docs generados">{p.document_count}</td>
                  <td data-label="Fecha">{formatDate(p.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination} role="navigation" aria-label="Paginación proyectos">
          <button
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            aria-label="Página anterior"
          >
            ← Anterior
          </button>
          <span className={styles.pageInfo}>
            Página {page} de {totalPages} ({total} proyectos)
          </span>
          <button
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            aria-label="Página siguiente"
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  )
}
